/**
 * Cascade cleanup for everything that points at a recipe by id.
 *
 * A saved recipe's id is referenced from five other places. Deleting the
 * `savedRecipes` document alone leaves all five dangling — the planned meal
 * that can no longer rehydrate, the rating whose recipe no longer exists, the
 * cook history nothing can reach. `unifiedRecipeService.deleteRecipe()` did
 * exactly that until this module existed.
 *
 *   {scope}/mealPlan/{weekStart}       meals[].recipeId
 *   {scope}/recipeMetadata/{recipeId}  doc id IS the recipe id
 *   {scope}/cookLog/{recipeId}         doc id IS the recipe id
 *   {scope}/preferences/recipes        hidden[], favorites[]
 *   {scope}/groceryList/current        items[].recipeIds, customItems[].recipeIds
 *
 * Two entry points, because the two callers want opposite things:
 *
 *   reassignRecipeReferences  — de-duplication. The recipe still exists, under
 *                               a different id. Repoint, never destroy.
 *   purgeRecipeReferences     — deletion. The recipe is gone. Remove the
 *                               references so nothing dangles.
 */

import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  arrayRemove,
  arrayUnion,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { resolveDataScope, buildScopedPath, type DataScope } from './householdPaths';
import { entriesOfCookLogDoc, toCloudEntry } from './cookLogSyncService';

export interface ReferenceCleanupResult {
  metadataMerged: boolean;
  metadataDeleted: boolean;
  cookLogEntriesMoved: number;
  cookLogDeleted: boolean;
  preferencesUpdated: boolean;
  plannedMealsTouched: number;
  groceryItemsTouched: number;
}

const emptyResult = (): ReferenceCleanupResult => ({
  metadataMerged: false,
  metadataDeleted: false,
  cookLogEntriesMoved: 0,
  cookLogDeleted: false,
  preferencesUpdated: false,
  plannedMealsTouched: 0,
  groceryItemsTouched: 0,
});

const scopedDoc = (scope: DataScope, ...segments: string[]) => {
  if (!firestore) throw new Error('Firestore not initialized');
  const [root, id, ...rest] = buildScopedPath(scope, ...segments);
  return doc(firestore, root, id, ...rest);
};

const scopedCollection = (scope: DataScope, ...segments: string[]) => {
  if (!firestore) throw new Error('Firestore not initialized');
  const [root, id, ...rest] = buildScopedPath(scope, ...segments);
  return collection(firestore, root, id, ...rest);
};

/** Firestore rejects undefined; strip before writing. */
const clean = <T extends Record<string, unknown>>(obj: T): T => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out as T;
};

const maxDate = (a?: string, b?: string): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

/**
 * Merge the losing copy's metadata into the surviving one.
 *
 * Deliberately not last-write-wins: these are two records of the SAME recipe,
 * so the union is what the cook actually meant. A rating on either copy is a
 * rating; notes on both are both worth keeping.
 */
const mergeMetadata = (
  keep: Record<string, any> | undefined,
  drop: Record<string, any>,
  keepId: string
): Record<string, unknown> => {
  if (!keep) return { ...drop, recipeId: keepId };

  const notes = [keep.notes, drop.notes].filter(Boolean);
  return clean({
    ...keep,
    recipeId: keepId,
    rating: Math.max(keep.rating ?? 0, drop.rating ?? 0) || undefined,
    notes: notes.length ? [...new Set(notes)].join('\n\n') : undefined,
    isFavorite: !!(keep.isFavorite || drop.isFavorite),
    timesPlanned: (keep.timesPlanned ?? 0) + (drop.timesPlanned ?? 0),
    lastMade: maxDate(keep.lastMade, drop.lastMade),
    lastViewed: maxDate(keep.lastViewed, drop.lastViewed),
    lastMadeBy: keep.lastMade && drop.lastMade
      ? (keep.lastMade >= drop.lastMade ? keep.lastMadeBy : drop.lastMadeBy)
      : (keep.lastMadeBy ?? drop.lastMadeBy),
  });
};

/**
 * A recipe's references are NOT necessarily in the same scope as the recipe.
 *
 * `resolveDataScope` prefers the household whenever the user is paired, so a
 * paired user's ratings and cook log are written to
 * `households/{hid}/recipeMetadata/{id}` even for a recipe whose document still
 * lives in `users/{uid}/savedRecipes/{id}` (legacy saves predating the
 * household, mostly). Deriving the scope from the recipe's own path therefore
 * looks in the wrong collection and silently leaves the metadata behind — the
 * same read-here/write-there mistake that caused #575 in the first place.
 *
 * Passing every scope the user can reach and sweeping all of them is the only
 * way to be sure. Order matters only in that the first entry is where merged
 * data lands when the destination has none.
 */
export type CleanupScopes = DataScope | DataScope[];

const asList = (scopes: CleanupScopes): DataScope[] =>
  Array.isArray(scopes) ? scopes : [scopes];

const sumInto = (a: ReferenceCleanupResult, b: ReferenceCleanupResult) => {
  a.metadataMerged ||= b.metadataMerged;
  a.metadataDeleted ||= b.metadataDeleted;
  a.cookLogEntriesMoved += b.cookLogEntriesMoved;
  a.cookLogDeleted ||= b.cookLogDeleted;
  a.preferencesUpdated ||= b.preferencesUpdated;
  a.plannedMealsTouched += b.plannedMealsTouched;
  a.groceryItemsTouched += b.groceryItemsTouched;
  return a;
};

/**
 * Rewrite every reference to `fromId` so it points at `toId`, then remove the
 * old side. Used when de-duplicating: the recipe survives under `toId`, so
 * nothing the cook created should be lost.
 */
export async function reassignRecipeReferences(
  scopes: CleanupScopes,
  fromId: string,
  toId: string
): Promise<ReferenceCleanupResult> {
  if (!isFirebaseEnabled() || !firestore) throw new Error('Firebase not enabled');
  if (fromId === toId) return emptyResult();
  const list = asList(scopes);
  if (list.length > 1) {
    const total = emptyResult();
    for (const s of list) sumInto(total, await reassignRecipeReferences(s, fromId, toId));
    return total;
  }
  const scope = list[0];
  const result = emptyResult();

  // ── recipeMetadata: merge into the survivor, then drop the loser ─────────
  const fromMetaRef = scopedDoc(scope, 'recipeMetadata', fromId);
  const fromMeta = await getDoc(fromMetaRef);
  if (fromMeta.exists()) {
    const toMetaRef = scopedDoc(scope, 'recipeMetadata', toId);
    const toMeta = await getDoc(toMetaRef);
    const merged = mergeMetadata(toMeta.exists() ? toMeta.data() : undefined, fromMeta.data()!, toId);
    await setDoc(toMetaRef, { ...merged, updatedAt: new Date().toISOString() }, { merge: true });
    await deleteDoc(fromMetaRef);
    result.metadataMerged = true;
    result.metadataDeleted = true;
  }

  // ── cookLog: move every entry onto the survivor ──────────────────────────
  // Both shapes are read (the legacy `entries` array and the per-entry `byId`
  // map, #795) and written into the survivor's map one key each, so nothing
  // already on the survivor is rewritten.
  const fromLogRef = scopedDoc(scope, 'cookLog', fromId);
  const fromLog = await getDoc(fromLogRef);
  if (fromLog.exists()) {
    const entries = entriesOfCookLogDoc(fromLog.data() ?? {});
    if (entries.length) {
      const toLogRef = scopedDoc(scope, 'cookLog', toId);
      const byId = Object.fromEntries(
        entries.map((e) => [e.id, toCloudEntry({ ...e, recipeId: toId })])
      );
      await setDoc(
        toLogRef,
        { recipeId: toId, byId, updatedAt: new Date().toISOString() },
        { merge: true }
      );
      result.cookLogEntriesMoved = entries.length;
    }
    await deleteDoc(fromLogRef);
    result.cookLogDeleted = true;
  }

  // ── preferences: swap the id inside hidden[] / favorites[] ───────────────
  const prefsRef = scopedDoc(scope, 'preferences', 'recipes');
  const prefs = await getDoc(prefsRef);
  if (prefs.exists()) {
    const data = prefs.data() ?? {};
    for (const field of ['hidden', 'favorites'] as const) {
      if (Array.isArray(data[field]) && data[field].includes(fromId)) {
        await updateDoc(prefsRef, { [field]: arrayRemove(fromId) });
        await updateDoc(prefsRef, { [field]: arrayUnion(toId) });
        result.preferencesUpdated = true;
      }
    }
  }

  // ── mealPlan: repoint across EVERY week bucket ───────────────────────────
  // Not just the bucket the date implies: the planner aggregates across all
  // buckets by date, so a meal can sit in a bucket other than its own week.
  const weeks = await getDocs(scopedCollection(scope, 'mealPlan'));
  for (const week of weeks.docs) {
    const meals = (week.data()?.meals ?? []) as Array<Record<string, unknown>>;
    if (!meals.some((m) => m.recipeId === fromId)) continue;
    const rewritten = meals.map((m) => (m.recipeId === fromId ? { ...m, recipeId: toId } : m));
    await updateDoc(week.ref, { meals: rewritten, updatedAt: new Date().toISOString() });
    result.plannedMealsTouched += meals.filter((m) => m.recipeId === fromId).length;
  }

  // ── groceryList: swap the id inside each item's recipeIds ────────────────
  const groceryRef = scopedDoc(scope, 'groceryList', 'current');
  const grocery = await getDoc(groceryRef);
  if (grocery.exists()) {
    const data = grocery.data() ?? {};
    let touched = 0;
    const rewrite = (items: Array<Record<string, any>> | undefined) =>
      (items ?? []).map((item) => {
        const ids: string[] = item.recipeIds ?? [];
        if (!ids.includes(fromId)) return item;
        touched++;
        return { ...item, recipeIds: [...new Set(ids.map((i) => (i === fromId ? toId : i)))] };
      });
    const items = rewrite(data.items);
    const customItems = rewrite(data.customItems);
    if (touched > 0) {
      await updateDoc(groceryRef, { items, customItems, updatedAt: new Date().toISOString() });
      result.groceryItemsTouched = touched;
    }
  }

  return result;
}

/**
 * Remove every reference to a recipe that is being deleted outright.
 *
 * Unlike reassign, this destroys: a planned meal for a recipe that no longer
 * exists cannot render, so leaving it would only produce a permanently broken
 * row in the planner.
 *
 * The scopes, and the five collections inside each, touch different documents,
 * so they are swept at once. One after another it was ~20 round trips, and on a
 * phone the cook waited 30 s on a delete that showed nothing (#793).
 */
export async function purgeRecipeReferences(
  scopes: CleanupScopes,
  recipeId: string
): Promise<ReferenceCleanupResult> {
  if (!isFirebaseEnabled() || !firestore) throw new Error('Firebase not enabled');
  const list = asList(scopes);
  if (list.length > 1) {
    const total = emptyResult();
    const results = await Promise.all(list.map((s) => purgeRecipeReferences(s, recipeId)));
    for (const r of results) sumInto(total, r);
    return total;
  }
  const scope = list[0];
  const result = emptyResult();

  const purgeMetadata = async () => {
    const metaRef = scopedDoc(scope, 'recipeMetadata', recipeId);
    if ((await getDoc(metaRef)).exists()) {
      await deleteDoc(metaRef);
      result.metadataDeleted = true;
    }
  };

  const purgeCookLog = async () => {
    const logRef = scopedDoc(scope, 'cookLog', recipeId);
    if ((await getDoc(logRef)).exists()) {
      await deleteDoc(logRef);
      result.cookLogDeleted = true;
    }
  };

  const purgePreferences = async () => {
    const prefsRef = scopedDoc(scope, 'preferences', 'recipes');
    const prefs = await getDoc(prefsRef);
    if (!prefs.exists()) return;
    const data = prefs.data() ?? {};
    const patch: Record<string, ReturnType<typeof arrayRemove>> = {};
    for (const field of ['hidden', 'favorites'] as const) {
      if (Array.isArray(data[field]) && data[field].includes(recipeId)) {
        patch[field] = arrayRemove(recipeId);
      }
    }
    if (Object.keys(patch).length > 0) {
      await updateDoc(prefsRef, patch);
      result.preferencesUpdated = true;
    }
  };

  const purgeMealPlan = async () => {
    const weeks = await getDocs(scopedCollection(scope, 'mealPlan'));
    await Promise.all(weeks.docs.map(async (week) => {
      const meals = (week.data()?.meals ?? []) as Array<Record<string, unknown>>;
      const remaining = meals.filter((m) => m.recipeId !== recipeId);
      if (remaining.length === meals.length) return;
      result.plannedMealsTouched += meals.length - remaining.length;
      await updateDoc(week.ref, { meals: remaining, updatedAt: new Date().toISOString() });
    }));
  };

  const purgeGroceryList = async () => {
    const groceryRef = scopedDoc(scope, 'groceryList', 'current');
    const grocery = await getDoc(groceryRef);
    if (!grocery.exists()) return;
    const data = grocery.data() ?? {};
    let touched = 0;
    // Drop the recipe's claim on an item; drop the item only when nothing else
    // wanted it AND the cook didn't add it by hand.
    const strip = (items: Array<Record<string, any>> | undefined, isCustom: boolean) =>
      (items ?? []).reduce<Array<Record<string, any>>>((acc, item) => {
        const ids: string[] = item.recipeIds ?? [];
        if (!ids.includes(recipeId)) {
          acc.push(item);
          return acc;
        }
        touched++;
        const remaining = ids.filter((i) => i !== recipeId);
        if (remaining.length === 0 && !isCustom) return acc;
        acc.push({ ...item, recipeIds: remaining });
        return acc;
      }, []);
    const items = strip(data.items, false);
    const customItems = strip(data.customItems, true);
    if (touched > 0) {
      await updateDoc(groceryRef, { items, customItems, updatedAt: new Date().toISOString() });
      result.groceryItemsTouched = touched;
    }
  };

  await Promise.all([
    purgeMetadata(),
    purgeCookLog(),
    purgePreferences(),
    purgeMealPlan(),
    purgeGroceryList(),
  ]);

  return result;
}

/** Convenience wrapper for callers that hold a uid + householdId rather than a scope. */
export const resolveScopeOrThrow = (
  userId: string | null | undefined,
  householdId: string | null | undefined
): DataScope => {
  const scope = resolveDataScope(userId, householdId);
  if (!scope) throw new Error('Cannot resolve data scope: missing userId and householdId');
  return scope;
};

/**
 * Every scope a user's own data can be sitting in — household first (where a
 * paired user's metadata is written), then personal (where older recipes and
 * their pre-pairing metadata remain).
 *
 * Cleanup must sweep both. `resolveDataScope` returns only the active one,
 * which is right for writing and wrong for cleaning up.
 */
export const resolveAllUserScopes = (
  userId: string | null | undefined,
  householdId: string | null | undefined
): DataScope[] => {
  const scopes: DataScope[] = [];
  if (householdId) scopes.push({ kind: 'household', householdId });
  if (userId) scopes.push({ kind: 'user', uid: userId });
  if (!scopes.length) throw new Error('Cannot resolve data scope: missing userId and householdId');
  return scopes;
};
